/**
 * When a job is booked for, and how every screen words it (PR-43, the v7 design Bobby
 * approved on 1 Nov 2026). The same rules as the phone (Checkover/Models/JobSchedule.swift)
 * and the server's one unit (functions/jobsCore.js `cleanSchedule`): a start, an optional
 * window end, and whether only the day counts.
 *
 * - No start: nobody dated the job. Live Jobs shows a grey "ASAP" tile; the job page and
 *   forms say "Not set".
 * - A start today: blue "TODAY", no Pre-booked label.
 * - A later day: amber with the month, Pre-booked.
 * - An earlier day (a booking that has passed): the date, in the neutral grey.
 *
 * Times follow the browser's clock (UK operators).
 */

export type ScheduleKind = 'asap' | 'today' | 'preBooked' | 'past';

type TimestampLike = { toMillis(): number } | { toDate(): Date } | Date | number | null | undefined;

export interface Schedule {
  start: Date | null;
  end: Date | null;
  dateOnly: boolean;
}

function toDate(value: TimestampLike): Date | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if ('toMillis' in value && typeof value.toMillis === 'function') return new Date(value.toMillis());
  if ('toDate' in value && typeof value.toDate === 'function') return value.toDate();
  return null;
}

export function jobSchedule(job: {
  scheduledFor?: TimestampLike;
  scheduledUntil?: TimestampLike;
  scheduledDateOnly?: boolean | null;
}): Schedule {
  const start = toDate(job.scheduledFor);
  const dateOnly = start !== null && job.scheduledDateOnly === true;
  return { start, end: dateOnly || !start ? null : toDate(job.scheduledUntil), dateOnly };
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export function scheduleKind(s: Schedule, now = new Date()): ScheduleKind {
  if (!s.start) return 'asap';
  const day = startOfDay(s.start).getTime();
  const today = startOfDay(now).getTime();
  if (day === today) return 'today';
  return day > today ? 'preBooked' : 'past';
}

/** "10am", "11:30am", "12pm", "12am": twelve-hour, minutes only when not on the hour. */
export function clock(d: Date): { time: string; suffix: 'am' | 'pm' } {
  const h24 = d.getHours();
  const m = d.getMinutes();
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return { time: m === 0 ? `${h}` : `${h}:${String(m).padStart(2, '0')}`, suffix: h24 < 12 ? 'am' : 'pm' };
}

/** The time split where a window wraps in the tile: ["10:30am", "–1:30pm"]. null for no time. */
export function timeLines(s: Schedule): string[] | null {
  if (!s.start || s.dateOnly) return null;
  const from = clock(s.start);
  if (!s.end) return [from.time + from.suffix];
  const to = clock(s.end);
  const first = from.suffix === to.suffix ? from.time : from.time + from.suffix;
  return [first, `–${to.time}${to.suffix}`];
}

/** "10am", "10am–1pm", "9–11am", "10:30am–1:30pm"; null when no time was set. */
export function timeText(s: Schedule): string | null {
  return timeLines(s)?.join('') ?? null;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The tile's small top line: "DEC", or "TODAY". null for ASAP. */
export function tileLabel(s: Schedule, now = new Date()): string | null {
  if (!s.start) return null;
  return scheduleKind(s, now) === 'today' ? 'TODAY' : MONTHS[s.start.getMonth()].toUpperCase();
}

export function tileDay(s: Schedule): string | null {
  return s.start ? String(s.start.getDate()) : null;
}

/** The job page's one-line banner: "Wed 31 Dec · 10:30am–1:30pm", "Today · 11:30am", "Today". */
export function bannerText(s: Schedule, now = new Date()): string | null {
  if (!s.start) return null;
  const day = scheduleKind(s, now) === 'today'
    ? 'Today'
    : `${DAYS[s.start.getDay()]} ${s.start.getDate()} ${MONTHS[s.start.getMonth()]}`;
  const time = timeText(s);
  return time ? `${day} · ${time}` : day;
}

export function scheduleLabel(s: Schedule, now = new Date()): string {
  if (!s.start) return 'No date set, as soon as possible';
  const kind = scheduleKind(s, now);
  let spoken: string | null = null;
  if (s.start && !s.dateOnly) {
    const from = clock(s.start);
    spoken = s.end ? `${from.time}${from.suffix} to ${clock(s.end).time}${clock(s.end).suffix}` : from.time + from.suffix;
  }
  if (kind === 'today') return spoken ? `Today, ${spoken}` : 'Today';
  const day = `${DAYS_LONG[s.start.getDay()]} ${s.start.getDate()} ${MONTHS_LONG[s.start.getMonth()]}`;
  return `${kind === 'preBooked' ? 'Pre-booked, ' : 'Booked for '}${day}${spoken ? `, ${spoken}` : ''}`;
}

/** Live Jobs order: everything not pre-booked first (order kept), then pre-booked, soonest first. */
export function preBookedLast<T extends Parameters<typeof jobSchedule>[0]>(jobs: T[], now = new Date()): T[] {
  const later: { job: T; at: number }[] = [];
  const rest: T[] = [];
  for (const job of jobs) {
    const s = jobSchedule(job);
    if (scheduleKind(s, now) === 'preBooked' && s.start) later.push({ job, at: s.start.getTime() });
    else rest.push(job);
  }
  return later.length === 0 ? jobs : [...rest, ...later.sort((a, b) => a.at - b.at).map((x) => x.job)];
}

// ---------------------------------------------------------------------------
// The form's booking (New Job and the job page): what people pick, before it becomes
// the server's three fields.
// ---------------------------------------------------------------------------

export type TimeMode = 'anyTime' | 'at' | 'between';

export interface BookingForm {
  /** "YYYY-MM-DD", or '' for Not set. */
  date: string;
  mode: TimeMode;
  /** "HH:MM" (24-hour, from <input type="time">), or ''. */
  at: string;
  from: string;
  to: string;
}

export const EMPTY_BOOKING: BookingForm = { date: '', mode: 'anyTime', at: '', from: '', to: '' };

const pad = (n: number) => String(n).padStart(2, '0');
const dateKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeKey = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

export function todayKey(now = new Date()): string {
  return dateKey(now);
}

export function bookingFromSchedule(s: Schedule): BookingForm {
  if (!s.start) return EMPTY_BOOKING;
  const date = dateKey(s.start);
  if (s.dateOnly) return { ...EMPTY_BOOKING, date };
  if (s.end) return { ...EMPTY_BOOKING, date, mode: 'between', from: timeKey(s.start), to: timeKey(s.end) };
  return { ...EMPTY_BOOKING, date, mode: 'at', at: timeKey(s.start) };
}

function at(date: string, time: string): Date | null {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  if (!y || !mo || !d || Number.isNaN(h) || Number.isNaN(mi)) return null;
  return new Date(y, mo - 1, d, h, mi);
}

/** The booking as the screens word it; an unfinished At or Between counts as Any time. */
export function bookingSchedule(b: BookingForm): Schedule {
  if (!b.date) return { start: null, end: null, dateOnly: false };
  const day = at(b.date, '00:00');
  if (b.mode === 'at' && b.at) return { start: at(b.date, b.at), end: null, dateOnly: false };
  if (b.mode === 'between' && b.from && b.to) return { start: at(b.date, b.from), end: at(b.date, b.to), dateOnly: false };
  return { start: day, end: null, dateOnly: true };
}

export function bookingWindowIsBackwards(b: BookingForm): boolean {
  const s = bookingSchedule(b);
  return !!(s.start && s.end && s.end.getTime() <= s.start.getTime());
}

/** The server's fields, always all three (it stores the booking as one unit). */
export function bookingFields(b: BookingForm): {
  scheduledFor: number | null;
  scheduledUntil: number | null;
  scheduledDateOnly: boolean;
} {
  const s = bookingSchedule(b);
  return {
    scheduledFor: s.start ? s.start.getTime() : null,
    scheduledUntil: s.end ? s.end.getTime() : null,
    scheduledDateOnly: s.dateOnly,
  };
}

export function sameBooking(a: BookingForm, b: BookingForm): boolean {
  const x = bookingFields(a);
  const y = bookingFields(b);
  return x.scheduledFor === y.scheduledFor && x.scheduledUntil === y.scheduledUntil && x.scheduledDateOnly === y.scheduledDateOnly;
}
